import { useTranslation } from "react-i18next";
import { Card, ErrorBox, Skeleton } from "./ui";
import { useMyDay, usePunch } from "../lib/api";

/** Clock in and out (HRX-02).
 *
 * Before this there was no way to clock anyone in or out at all: `Employee Checkin` had no rows and every
 * attendance record was a bare day status. The punch is for oneself only — a supervisor marking somebody else
 * present is a different act, and it belongs on an attendance record with their name against it. */
export default function TimeClock() {
  const { t } = useTranslation();
  const q = useMyDay();
  const punch = usePunch();

  if (q.isError) return <div><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <Skeleton className="h-32" />;
  const d = q.data;

  if (!d.employee) {
    return <Card className="p-4 text-sm text-brand-grey" data-timeclock>{t("my.clock.noEmployee")}</Card>;
  }
  if (!d.can_punch || !d.shift) {
    return <Card className="p-4 text-sm text-brand-grey" data-timeclock>{t("my.clock.noShift")}</Card>;
  }

  const day = d.day;
  const arriving = d.next === "IN";

  return (
    <Card className="p-4" data-timeclock>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-base font-semibold">{t("my.clock.title")}</h3>
          <p className="mt-0.5 text-[11px] text-brand-grey">
            {t("my.clock.shift", { shift: d.shift.name, from: String(d.shift.start_time).slice(0, 5), to: String(d.shift.end_time).slice(0, 5) })}
          </p>
        </div>
        <button onClick={() => punch.mutate({})} disabled={punch.isPending} data-timeclock-punch={d.next}
          className={`rounded px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${arriving ? "bg-status-green" : "bg-brand-primary"}`}>
          {punch.isPending ? t("my.clock.working") : arriving ? t("my.clock.in") : t("my.clock.out")}
        </button>
      </div>

      {day && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4" data-timeclock-day>
          <Fact label={t("my.clock.firstIn")} value={day.first_in ? day.first_in.slice(11, 16) : "–"}
            tone={day.late_entry ? "amber" : undefined}
            hint={day.late_entry ? t("my.clock.lateBy", { minutes: day.late_by_minutes }) : undefined} />
          <Fact label={t("my.clock.lastOut")} value={day.last_out ? day.last_out.slice(11, 16) : (day.on_site ? t("my.clock.onSite") : "–")}
            tone={day.early_exit ? "amber" : undefined} />
          <Fact label={t("my.clock.hours")} value={`${day.hours.toFixed(2)} h`}
            hint={t("my.clock.ofScheduled", { hours: day.scheduled_hours })} />
          <Fact label={t("my.clock.status")} value={t(`my.clock.statuses.${day.status}`, day.status)}
            tone={day.status === "Absent" ? "amber" : undefined}
            hint={day.overtime_hours ? t("my.clock.overtime", { hours: day.overtime_hours }) : undefined} />
        </div>
      )}

      {d.punches.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1 text-[11px]">
          {d.punches.map((p) => (
            <span key={p.name} className={`rounded px-2 py-0.5 ${p.log_type === "IN" ? "bg-green-50 text-status-green" : "bg-brand-grey-light text-brand-charcoal"}`}>
              {p.log_type} {p.time.slice(11, 16)}
            </span>
          ))}
        </div>
      )}

      <p className="mt-2 text-[11px] text-brand-grey">{t("my.clock.hint")}</p>
      {punch.isError && <div className="mt-2"><ErrorBox error={punch.error} /></div>}
    </Card>
  );
}

function Fact({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "amber" }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      <div className={`font-heading text-lg font-semibold ${tone === "amber" ? "text-status-amber" : ""}`}>{value}</div>
      {hint && <div className="text-[11px] text-brand-grey">{hint}</div>}
    </div>
  );
}
